import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import MobileTabNavigation from './MobileTabNavigation';
import Sidebar from '../Sidebar';
import ErrorBoundary from '../ErrorBoundary';
import BackorderAlertBanner from '../BackorderAlertBanner';
import { useAuth } from '../../contexts/AuthContext';
import SessionExpiredModal from '../auth/SessionExpiredModal';
import { legacyMainProps } from './migratedRoutes';
import { rememberLastRoute } from '../../utils/landingRoute';
import { ToolHost, ToolHostProvider } from './tools/ToolHost';
import { PaletteHostProvider } from './palette/paletteHostStore';
import { boundaryPrefixes, moduleForPath } from '../../modules/registry';
import { SHELL_MAIN_ID, shellScrollTo } from './shellScroll';
import { ASSISTANT_FOOTPRINT, useAssistantLauncherHidden } from '../AIAssistant/assistantLauncher';
import './Shell.css';

const MOBILE_BREAKPOINT = 1024;

/**
 * Modules that route inside themselves keep one page instance across their own URLs: their
 * boundary prefixes come from the module registry (G0). Every other route is keyed by its full
 * path and remounts exactly as before.
 */
export const boundaryKeyFor = (pathname, prefixes = boundaryPrefixes()) => {
  const owner = prefixes.find(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  return owner || pathname;
};

const MainLayout = ({ children }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [authExpired, setAuthExpired] = useState(false);
  const [authExpiredMessage, setAuthExpiredMessage] = useState('');
  const authExpiredHandledRef = useRef(false);
  const redirectTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  // Where the assistant's launcher floats, <main> keeps its footprint clear at its foot (tablet
  // pass 2026-10-20, item 1), so the last control can scroll out from under it.
  const launcherHidden = useAssistantLauncherHidden();

  // Remember where this user is, so their next sign-in returns them here rather than
  // always to Quotations. Recorded from inside the authenticated shell, so public routes
  // can never be stored, and keyed per user id — a shop-floor terminal is shared, and
  // landing on the previous person's last screen would be both confusing and a leak.
  useEffect(() => {
    rememberLastRoute(user?.id, location.pathname);
  }, [user?.id, location.pathname]);

  // <main> owns the scrolling (viewport-locked shell, polish 4), and nothing reset the window
  // before either: a new page starts at the top. Moves inside one module (/quotes -> /quotes/12)
  // are the module's own (useQuoteRoute keeps the list's place on Back); entering a module from
  // elsewhere resets like any page. Query-only changes never move the page.
  const lastPathRef = useRef(location.pathname);
  useLayoutEffect(() => {
    const from = lastPathRef.current;
    const to = location.pathname;
    lastPathRef.current = to;
    if (from === to) return;
    const owner = moduleForPath(to);
    if (owner && owner === moduleForPath(from)) return;
    shellScrollTo(0);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleAuthExpired = (_event) => {
      if (authExpiredHandledRef.current) return;
      authExpiredHandledRef.current = true;

      setAuthExpired(true);
      setAuthExpiredMessage('Your session has expired. Please log in again.');

      redirectTimerRef.current = setTimeout(() => {
        logout();
        navigate('/login', { replace: true, state: { reason: 'expired' } });
      }, 1500);
    };

    window.addEventListener('auth:expired', handleAuthExpired);
    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, [logout, navigate]);

  const legacy = legacyMainProps(location.pathname);

  return (
    <ToolHostProvider><PaletteHostProvider>
      {/* Viewport-locked (polish 4; Shell.css): the page never scrolls; the sidebar scrolls
          within itself and <main> owns the content's scrolling. */}
      <div className="crm-shell">
        {/* Header */}
        <Header isMobile={isMobile} />

        {authExpired && (
          <div style={{
            background: 'var(--status-error-bg)',
            borderBottom: '1px solid var(--status-error-border)',
            color: 'var(--status-error)',
            padding: '10px 16px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div style={{ fontWeight: 600 }}>{authExpiredMessage}</div>
            <button
              onClick={() => {
                if (redirectTimerRef.current) {
                  clearTimeout(redirectTimerRef.current);
                }
                logout();
                navigate('/login', { replace: true, state: { reason: 'expired' } });
              }}
              style={{
                padding: '6px 10px',
                background: 'var(--status-error-solid)',
                color: 'var(--text-on-accent)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Log In Again
            </button>
          </div>
        )}

        {/* Backorder Alert Banner */}
        <BackorderAlertBanner />

        {/* Main Content Area: the rest of the viewport */}
        <div className="crm-shell-body">
          {/* Desktop Sidebar */}
          {!isMobile && (
            <Sidebar isLayoutMode={true} />
          )}

          {/* Page Content.
              legacyMainProps() puts NOT-yet-migrated routes in a local data-theme="light"
              context (+ .legacy-light) so the global dark-theme light-text rule can't
              render invisible text on their still-hardcoded light backgrounds. Migrated
              routes (see migratedRoutes.js allowlist) get {} and follow the app theme.
              ToolHost (S5) puts <main> in the content area with the Z5 tool panel beside it
              and the Z6 rail to its right, when the page has registered tools. */}
          <ToolHost>
            <main
              {...legacy}
              id={SHELL_MAIN_ID}
              className={legacy.className ? `crm-shell-main ${legacy.className}` : 'crm-shell-main'}
              style={{
                // Space for the mobile nav, or the assistant's launcher where it floats.
                paddingBottom: isMobile ? '80px' : launcherHidden ? '0' : `${ASSISTANT_FOOTPRINT}px`,
              }}
            >
              {/* Route-scoped error boundary: a render error in a single page shows
                  an inline fallback while keeping header/sidebar/nav functional.
                  Keyed by module, not by path, for modules that route inside themselves
                  (boundaryKeyFor): /quotes → /quotes/12 must not remount the page.
                  resetKey still clears an error on every path change. */}
              <ErrorBoundary
                scope="route"
                key={boundaryKeyFor(location.pathname)}
                resetKey={location.pathname}
              >
                {children || <Outlet />}
              </ErrorBoundary>
              {/* The footer scrolls with the content, on desktop. Not on a module's route: its
                  split is bounded to the viewport, so a footer could only be reached by
                  scrolling the split's own frame (polish 4). */}
              {!isMobile && !moduleForPath(location.pathname) && <Footer />}
            </main>
          </ToolHost>
        </div>

        {/* Mobile Bottom Navigation */}
        {isMobile && <MobileTabNavigation />}

        {/* Session Expired Modal — shown globally when idle timeout triggers */}
        <SessionExpiredModal />
      </div>
    </PaletteHostProvider></ToolHostProvider>
  );
};

export default MainLayout;
